import axios from 'axios';
import type {
  CropData,
  DualUploadResult,
  PdfGenerationResult,
  MockupGenerationResult,
  DriveTokenResponse,
  BunnyCredentialsResponse,
  ApiResponse,
  ProductCategory,
} from '../types';
import { AssetGenerator } from './AssetGenerator';
import { DriveUploadService, BunnyUploadService } from './upload';
import { PdfService } from './PdfService';

export class ListingOrchestrator {
  private baseUrl: string;
  private assetGenerator: AssetGenerator;
  private driveUploadService: DriveUploadService;
  private bunnyUploadService: BunnyUploadService;
  private pdfService: PdfService;

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl;
    this.assetGenerator = new AssetGenerator();
    this.driveUploadService = new DriveUploadService();
    this.bunnyUploadService = new BunnyUploadService();
    this.pdfService = new PdfService();
  }

  async startSession(file: File): Promise<{ success: boolean; error?: string }> {
    return this.assetGenerator.validateImage(file);
  }

  async generateAssets(masterImage: File, crops: CropData[]): Promise<CropData[]> {
    return this.assetGenerator.generateCroppedAssets(masterImage, crops);
  }

  async executeDualUpload(
    crops: CropData[],
    productTitle: string,
    productCategory?: ProductCategory
  ): Promise<DualUploadResult> {
    const [driveToken, bunnyCredentials] = await Promise.all([
      this.getDriveToken(),
      this.getBunnyCredentials(),
    ]);

    const fetchReadmePdf = productCategory
      ? (category: ProductCategory) => this.pdfService.fetchReadmePdf(category, () => this.getSettings())
      : undefined;

    const [driveResult, bunnyResult] = await Promise.all([
      this.driveUploadService.uploadCrops(crops, driveToken, productTitle, fetchReadmePdf, productCategory),
      this.bunnyUploadService.uploadCrops(crops, bunnyCredentials, productTitle, fetchReadmePdf, productCategory),
    ]);

    return { drive: driveResult, bunny: bunnyResult };
  }

  async getBunnyCredentials(): Promise<BunnyCredentialsResponse> {
    const response = await axios.get<ApiResponse<BunnyCredentialsResponse>>(
      `${this.baseUrl}/keys/bunny`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get Bunny credentials');
    }

    return response.data.data;
  }

  async injectPdf(driveLink: string, productTitle: string): Promise<PdfGenerationResult> {
    const templateUrl = await this.getPdfTemplateUrl();
    const credentials = await this.getBunnyCredentials();

    return this.pdfService.generateDeliveryPdf(
      driveLink,
      productTitle,
      templateUrl,
      (blob, fileName, contentType, creds) => 
        this.bunnyUploadService.uploadFile(blob, fileName, contentType, creds),
      credentials
    );
  }

  async triggerRemoteMockupGeneration(
    imageUrl: string,
    productTitle: string
  ): Promise<MockupGenerationResult> {
    const response = await axios.post<ApiResponse<MockupGenerationResult>>(
      `${this.baseUrl}/proxy/mockup`,
      { imageUrl, productTitle }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Mockup generation request failed');
    }

    return response.data.data;
  }

  async saveToDatabase(data: {
    title: string;
    price: string;
    googleDriveLink: string;
    bunnyBackupSlug: string;
    finalPdfUrl: string;
    mockupUrls: string[];
    printFileUrls: Record<string, string>;
  }): Promise<ApiResponse> {
    const response = await axios.post<ApiResponse>(
      `${this.baseUrl}/products/save`,
      data
    );
    return response.data;
  }

  exportToCsv(data: {
    title: string;
    price: string;
    mockupUrls: string[];
    finalPdfUrl: string;
    googleDriveLink: string;
  }): string {
    const row = [
      data.title,
      data.price,
      data.mockupUrls[0] || '',
      data.mockupUrls[1] || '',
      data.mockupUrls[2] || '',
      data.finalPdfUrl,
      data.googleDriveLink,
    ];
    return row.map(field => `"${field}"`).join(',');
  }

  private async getDriveToken(): Promise<string> {
    const response = await axios.get<ApiResponse<DriveTokenResponse>>(
      `${this.baseUrl}/auth/get-drive-token`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get Drive token');
    }

    return response.data.data.accessToken;
  }

  private async getSettings(): Promise<{ categoryReadmePdfs?: Record<string, string> }> {
    const response = await axios.get(`${window.location.origin}/api/settings`);
    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get settings');
    }
    return response.data.data;
  }

  private async getPdfTemplateUrl(): Promise<string> {
    return 'https://example.com/template.pdf';
  }
}
